import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Heart, 
  Stethoscope, 
  HelpCircle, 
  Leaf, 
  GraduationCap, 
  Gem, 
  Target, 
  Dog 
} from 'lucide-react';

const profiles = [
  {
    id: 'sano',
    icon: Heart,
    title: 'Si tu perro o tu gato está sano y quieres que siga así',
    description: 'Prefieres prevenir antes que curar. Una pauta de nutrición natural diseñada para su edad, su peso y su estilo de vida es la inversión más inteligente en su salud a largo plazo.',
  },
  {
    id: 'enfermedad',
    icon: Stethoscope,
    title: 'Si tiene una enfermedad y la alimentación puede ayudarle',
    description: 'Digestivos, piel, riñón, hígado, articulaciones, sobrepeso, alergias... La dieta es parte del tratamiento. La diseño con criterio veterinario.',
  },
  {
    id: 'cansado',
    icon: HelpCircle,
    title: 'Si estás cansado de probar piensos y dietas sin saber si funcionan',
    description: 'Cambios de marca, vómitos, picores, heces blandas, falta de energía. Una pauta individualizada te da un plan claro en lugar de ensayo y error.',
  },
  {
    id: 'cuidas',
    icon: Leaf,
    title: 'Si cuidas tu propia alimentación y no concibes que tu animal coma de otra manera',
    description: 'Lees etiquetas, eliges ingredientes de calidad y sabes que lo que se come condiciona cómo se vive. Quieres aplicar esa misma coherencia a quien comparte tu vida.',
  },
  {
    id: 'criterio',
    icon: GraduationCap,
    title: 'Si buscas criterio profesional, no una receta de internet',
    description: 'Prefieres que un veterinario estudie el caso de tu animal a seguir una plantilla genérica. Valoras el rigor, la personalización y el seguimiento.',
  },
  {
    id: 'inversion',
    icon: Gem,
    title: 'Si su salud es una prioridad y estás dispuesto a invertir en ella',
    description: 'No buscas lo más barato, sino lo que de verdad funciona. Sabes que una buena nutrición hoy evita problemas (y facturas) mañana.',
  },
  {
    id: 'constante',
    icon: Target,
    title: 'Si eres constante y te implicas en su cuidado diario',
    description: 'Estás dispuesto a preparar, medir y seguir una pauta. Los mejores resultados llegan con tutores comprometidos, y este servicio está pensado para ellos.',
  },
  {
    id: 'etapas',
    icon: Dog,
    title: 'Si quieres acertar en cada etapa de su vida',
    description: 'Cachorro o gatito que acaba de llegar, adulto recién adoptado o senior que empieza a notar los años: sus necesidades cambian, y una pauta adaptada a cada momento marca la diferencia en su desarrollo, su movilidad y su calidad de vida.',
  },
];

export const ForWhoServiceSection = () => {
  return (
    <section 
      className="py-20 md:py-28 bg-white"
      data-testid="for-who-service-section"
    >
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight mb-4">
            ¿Para quién es este servicio?
          </h2>
        </motion.div>

        {/* Grid 2x4 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {profiles.map((profile, index) => (
            <motion.div
              key={profile.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-[#F8FAF7] rounded-2xl p-6 border border-brand-sage/20 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
              data-testid={`profile-card-${profile.id}`}
            >
              <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center mb-4">
                <profile.icon className="w-6 h-6 text-brand-primary" />
              </div>
              <h3 className="font-manrope font-semibold text-text-primary text-base mb-3 leading-snug">
                {profile.title}
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                {profile.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Final phrase + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-text-primary text-lg md:text-xl font-medium mb-6 max-w-2xl mx-auto">
            Si te has reconocido en dos o más de estas situaciones, este servicio está hecho para ti.
          </p>
          <Link
            to="/como-funciona"
            className="btn-primary inline-flex items-center justify-center gap-2 group"
            data-testid="for-who-cta"
          >
            Reservar consulta
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ForWhoServiceSection;
